import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  Paper, 
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { toast } from 'react-toastify';
import { getSectionContent, updateSectionContent } from '../services/contentService';
import ImageUploader from './common/ImageUploader';
import PreviewIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import ColorLensIcon from '@mui/icons-material/ColorLens';

const commonFontFamilies = [
  'Arial', 'Verdana', 'Helvetica', 'Tahoma', 'Trebuchet MS', 'Times New Roman', 'Georgia', 
  'Garamond', 'Courier New', 'Brush Script MT', 'Calibri', 'Candara', 'Optima', 'Segoe UI', 
  'Roboto', 'Noto Sans', 'Open Sans', 'Montserrat', 'Lato', 'Poppins'
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hero-tabpanel-${index}`}
      aria-labelledby={`hero-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const HeroSectionEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [heroData, setHeroData] = useState({
    // Content fields
    title: '',
    titleSuffix: '',
    subtitle: '',
    supportingText: '',
    tagline: '',
    description: '',
    ctaText1: '',
    ctaLink1: '',
    ctaText2: '',
    ctaLink2: '',
    mainImageUrl: '',
    mainImageAlt: '',
    showBrandLogos: true,
    authorizedDealerTextContent: 'Authorized Dealer Of Leading Brands', // Example default content

    // Styling fields
    backgroundColor: '#F3F4F6',

    titleColor: '#1E3A8A',
    titleFontSize: 48, // e.g., text-5xl
    titleFontWeight: '700', // e.g., font-bold
    titleFontFamily: 'Arial',

    titleSuffixColor: '#1E3A8A', // Assuming same as title by default
    titleSuffixFontSize: 48, 
    titleSuffixFontWeight: '700', 
    titleSuffixFontFamily: 'Arial',

    subtitleColor: '#4B5563',
    subtitleFontSize: 24, // e.g., text-2xl
    subtitleFontWeight: '500', // e.g., font-medium
    subtitleFontFamily: 'Arial',

    supportingTextColor: '#374151', // For supportingText
    supportingTextFontSize: 16, // e.g., text-base
    supportingTextFontWeight: '400', // e.g., font-normal
    supportingTextFontFamily: 'Arial',

    taglineColor: '#374151', // For tagline
    taglineFontSize: 18, // e.g., text-lg
    taglineFontWeight: '400', 
    taglineFontFamily: 'Arial',

    descriptionColor: '#374151', // For description
    descriptionFontSize: 16, 
    descriptionFontWeight: '400', 
    descriptionFontFamily: 'Arial',

    ctaButton1BackgroundColor: '#2563EB',
    ctaButton1TextColor: '#FFFFFF',
    ctaButton1FontSize: 16,
    ctaButton1FontWeight: '500',
    ctaButton1FontFamily: 'Arial',

    ctaButton2BackgroundColor: '#10B981',
    ctaButton2TextColor: '#FFFFFF',
    ctaButton2FontSize: 16,
    ctaButton2FontWeight: '500',
    ctaButton2FontFamily: 'Arial',

    authorizedDealerTextColor: '#1F2937',
    authorizedDealerTextFontSize: 14, // e.g., text-sm
    authorizedDealerTextFontWeight: '500',
    authorizedDealerTextFontFamily: 'Arial',
  });

  // Color picker states
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    fetchHeroData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHeroData = async () => {
    setLoading(true);
    try {
      const data = await getSectionContent('heroSection');
      let processedData = data ? { ...heroData, ...data } : { ...heroData };

      const styleFields = [
        { keyPrefix: 'title', defaultSize: 48, defaultFamily: 'Arial' },
        { keyPrefix: 'titleSuffix', defaultSize: 48, defaultFamily: 'Arial' },
        { keyPrefix: 'subtitle', defaultSize: 24, defaultFamily: 'Arial' },
        { keyPrefix: 'supportingText', defaultSize: 16, defaultFamily: 'Arial' },
        { keyPrefix: 'tagline', defaultSize: 18, defaultFamily: 'Arial' },
        { keyPrefix: 'description', defaultSize: 16, defaultFamily: 'Arial' },
        { keyPrefix: 'ctaButton1', defaultSize: 16, defaultFamily: 'Arial' },
        { keyPrefix: 'ctaButton2', defaultSize: 16, defaultFamily: 'Arial' },
        { keyPrefix: 'authorizedDealerText', defaultSize: 14, defaultFamily: 'Arial' },
      ];

      styleFields.forEach(field => {
        const sizeKey = `${field.keyPrefix}FontSize`;
        const familyKey = `${field.keyPrefix}FontFamily`;

        // Process Font Size
        if (processedData[sizeKey] !== undefined && processedData[sizeKey] !== null) {
          if (typeof processedData[sizeKey] === 'string') {
            const parsed = parseFloat(processedData[sizeKey].replace('px', ''));
            processedData[sizeKey] = isNaN(parsed) ? field.defaultSize : parsed;
          } else if (typeof processedData[sizeKey] !== 'number') {
            processedData[sizeKey] = field.defaultSize;
          }
        } else {
          processedData[sizeKey] = field.defaultSize;
        }

        // Process Font Family
        if (typeof processedData[familyKey] !== 'string' || !processedData[familyKey]) {
          processedData[familyKey] = field.defaultFamily;
        }
      });

      setHeroData(processedData);
    } catch (error) {
      console.error('Error fetching hero data:', error);
      toast.error('Failed to load hero section data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (imageUrl) => {
    setHeroData(prevData => ({
      ...prevData,
      mainImageUrl: imageUrl
    }));
  };

  const handleInputChange = (e) => {
    const { name, value: rawValue, type } = e.target;
    let value = rawValue;

    const isFontSizeField = name && name.endsWith('FontSize');

    if (isFontSizeField && type === 'number') {
      if (rawValue === '') {
        value = ''; // Allow temporarily empty for better UX
      } else {
        const parsed = parseFloat(rawValue);
        value = isNaN(parsed) ? rawValue : parsed; // Keep as string if not a valid number, or let validation handle it
      }
    }

    setHeroData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setHeroData(prevData => ({
      ...prevData,
      [name]: checked
    }));
  };

  const handleColorPickerClick = (colorType, event) => {
    // Get position for color picker
    const rect = event.currentTarget.getBoundingClientRect();
    setColorPickerPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX
    });
    
    setActiveColorPicker(colorType);
  };

  const handleColorChange = (color) => {
    if (activeColorPicker) {
      setHeroData(prevData => ({
        ...prevData,
        [activeColorPicker]: color.hex
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSectionContent('heroSection', heroData);
      toast.success('Hero section updated successfully');
    } catch (error) {
      console.error('Error saving hero data:', error);
      toast.error('Failed to update hero section');
    } finally {
      setSaving(false);
    }
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Hero Section Editor
        </Typography>
        <Box>
          <Tooltip title="Preview">
            <IconButton onClick={togglePreview} sx={{ mr: 1 }}>
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} aria-label="hero section tabs">
        <Tab label="Content" />
        <Tab label="Styling" />
        <Tab label="Brand Logos" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Main Content
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                label="Title"
                name="title"
                value={heroData.title}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Title Suffix"
                name="titleSuffix"
                value={heroData.titleSuffix}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Subtitle"
                name="subtitle"
                value={heroData.subtitle}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Supporting Text"
                name="supportingText"
                value={heroData.supportingText}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Tagline"
                name="tagline"
                value={heroData.tagline}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Description"
                name="description"
                value={heroData.description}
                onChange={handleInputChange}
                multiline
                rows={4}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Authorized Dealer Text Content"
                name="authorizedDealerTextContent"
                value={heroData.authorizedDealerTextContent}
                onChange={handleInputChange}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Call to Action Buttons
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="CTA 1 Text"
                    name="ctaText1"
                    value={heroData.ctaText1}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="CTA 1 Link"
                    name="ctaLink1"
                    value={heroData.ctaLink1}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="CTA 2 Text"
                    name="ctaText2"
                    value={heroData.ctaText2}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="CTA 2 Link"
                    name="ctaLink2"
                    value={heroData.ctaLink2}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Hero Image
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                label="Image Alt Text"
                name="mainImageAlt"
                value={heroData.mainImageAlt}
                onChange={handleInputChange}
              />
              <Box sx={{ mt: 2 }}>
                <ImageUploader 
                  currentImageUrl={heroData.mainImageUrl} 
                  onImageUploaded={handleImageUpload}
                  folder="hero"
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Background Color */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb:3 }}>
              <Typography variant="h6" gutterBottom>Global Styles</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Background Color:</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    onClick={(e) => handleColorPickerClick('backgroundColor', e)}
                    startIcon={<ColorLensIcon />}
                    fullWidth
                  >
                    {heroData.backgroundColor}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {[
            { groupTitle: 'Title Styling', baseName: 'title' },
            { groupTitle: 'Title Suffix Styling', baseName: 'titleSuffix' },
            { groupTitle: 'Subtitle Styling', baseName: 'subtitle' },
            { groupTitle: 'Supporting Text Styling', baseName: 'supportingText' },
            { groupTitle: 'Tagline Styling', baseName: 'tagline' },
            { groupTitle: 'Description Styling', baseName: 'description' },
            { groupTitle: 'CTA Button 1 Styling', baseName: 'ctaButton1' },
            { groupTitle: 'CTA Button 2 Styling', baseName: 'ctaButton2' },
            { groupTitle: 'Authorized Dealer Text Styling', baseName: 'authorizedDealerText' },
          ].map(styleGroup => (
            <Grid item xs={12} md={6} key={styleGroup.baseName}>
              <Paper sx={{ p: 3, mb:3 }}>
                <Typography variant="h6" gutterBottom>{styleGroup.groupTitle}</Typography>
                <Grid container spacing={2} alignItems="center">
                  {/* Color Picker - CTA buttons have background and text color, others just text color */}
                  {styleGroup.baseName.includes('ctaButton') ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1">Background Color:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          onClick={(e) => handleColorPickerClick(`${styleGroup.baseName}BackgroundColor`, e)}
                          startIcon={<ColorLensIcon />}
                          fullWidth
                        >
                          {heroData[`${styleGroup.baseName}BackgroundColor`]}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1">Text Color:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          onClick={(e) => handleColorPickerClick(`${styleGroup.baseName}TextColor`, e)}
                          startIcon={<ColorLensIcon />}
                          fullWidth
                        >
                          {heroData[`${styleGroup.baseName}TextColor`]}
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1">Text Color:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          onClick={(e) => handleColorPickerClick(`${styleGroup.baseName}Color`, e)}
                          startIcon={<ColorLensIcon />}
                          fullWidth
                        >
                          {heroData[`${styleGroup.baseName}Color`]}
                        </Button>
                      </Grid>
                    </>
                  )}

                  {/* Font Size */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Font Size (px)"
                      type="number"
                      name={`${styleGroup.baseName}FontSize`}
                      value={heroData[`${styleGroup.baseName}FontSize`]}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      helperText="Enter pixel value"
                    />
                  </Grid>

                  {/* Font Family */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Font Family</InputLabel>
                      <Select
                        name={`${styleGroup.baseName}FontFamily`}
                        value={heroData[`${styleGroup.baseName}FontFamily`] || 'Arial'}
                        onChange={handleInputChange}
                        label="Font Family"
                      >
                        {commonFontFamilies.map(font => (
                          <MenuItem key={font} value={font}>{font}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Font Weight */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Font Weight</InputLabel>
                      <Select
                        name={`${styleGroup.baseName}FontWeight`}
                        value={heroData[`${styleGroup.baseName}FontWeight`] || '400'}
                        onChange={handleInputChange}
                        label="Font Weight"
                      >
                        <MenuItem value="100">100 (Thin)</MenuItem>
                        <MenuItem value="200">200 (Extra Light)</MenuItem>
                        <MenuItem value="300">300 (Light)</MenuItem>
                        <MenuItem value="400">400 (Normal)</MenuItem>
                        <MenuItem value="500">500 (Medium)</MenuItem>
                        <MenuItem value="600">600 (Semi Bold)</MenuItem>
                        <MenuItem value="700">700 (Bold)</MenuItem>
                        <MenuItem value="800">800 (Extra Bold)</MenuItem>
                        <MenuItem value="900">900 (Black)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Color Picker Dialog (absolute positioned) */}
        {activeColorPicker && (
          <Box sx={{ position: 'absolute', top: colorPickerPosition.top, left: colorPickerPosition.left, zIndex: 1200 }}>
            {/* Click-away listener */}
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 1199, // Ensure it's below the picker itself
              }}
              onClick={() => setActiveColorPicker(null)}
            />
            <Paper elevation={8} sx={{ p:1, position: 'relative', zIndex: 1200 }}>
              <ChromePicker
                color={heroData[activeColorPicker] || '#000000'}
                onChangeComplete={handleColorChange} // Using onChangeComplete for better performance
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => setActiveColorPicker(null)}
                sx={{ mt: 1, display: 'block', width: '100%' }}
              >
                Close
              </Button>
            </Paper>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
      </TabPanel>

      {/* Preview Section */}
      {showPreview && (
        <Paper sx={{ mt: 3, p: 2, position: 'relative' }}>
          <IconButton 
            onClick={togglePreview} 
            sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
          >
            <PreviewIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            Hero Section Preview
          </Typography>
          <Box sx={{ mt: 2, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                position: 'relative',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                backgroundColor: heroData.backgroundColor,
                color: heroData.textColor,
                textAlign: 'center'
              }}
            >
              {heroData.mainImageUrl && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${heroData.mainImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.7,
                    zIndex: 0
                  }}
                />
              )}
              
              <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '800px' }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: heroData.titleColor,
                    fontSize: heroData.titleFontSize ? `${heroData.titleFontSize}px` : undefined,
                    fontFamily: heroData.titleFontFamily,
                    fontWeight: heroData.titleFontWeight,
                    mb: 2,
                  }}
                >
                  {heroData.title}{' '}
                  {heroData.titleSuffix && (
                    <span
                      style={{
                        display: 'block',
                        color: heroData.titleSuffixColor,
                        fontSize: heroData.titleSuffixFontSize ? `${heroData.titleSuffixFontSize}px` : undefined,
                        fontFamily: heroData.titleSuffixFontFamily,
                        fontWeight: heroData.titleSuffixFontWeight,
                      }}
                    >
                      {heroData.titleSuffix}
                    </span>
                  )}
                </Typography>
                
                <Typography
                  variant="h5"
                  sx={{
                    color: heroData.subtitleColor,
                    fontSize: heroData.subtitleFontSize ? `${heroData.subtitleFontSize}px` : undefined,
                    fontFamily: heroData.subtitleFontFamily,
                    fontWeight: heroData.subtitleFontWeight,
                    mb: 2,
                  }}
                >
                  {heroData.subtitle}
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: heroData.supportingTextColor,
                    fontSize: heroData.supportingTextFontSize ? `${heroData.supportingTextFontSize}px` : undefined,
                    fontFamily: heroData.supportingTextFontFamily,
                    fontWeight: heroData.supportingTextFontWeight,
                    mb: 1,
                  }}
                >
                  {heroData.supportingText}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: heroData.taglineColor,
                    fontSize: heroData.taglineFontSize ? `${heroData.taglineFontSize}px` : undefined,
                    fontFamily: heroData.taglineFontFamily,
                    fontWeight: heroData.taglineFontWeight,
                    mb: 3,
                  }}
                >
                  {heroData.tagline}
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: heroData.descriptionColor,
                    fontSize: heroData.descriptionFontSize ? `${heroData.descriptionFontSize}px` : undefined,
                    fontFamily: heroData.descriptionFontFamily,
                    fontWeight: heroData.descriptionFontWeight,
                    mb: 4,
                    maxWidth: '600px',
                    mx: 'auto',
                  }}
                >
                  {heroData.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  {heroData.ctaText1 && (
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: heroData.ctaButton1BackgroundColor,
                        color: heroData.ctaButton1TextColor,
                        fontSize: heroData.ctaButton1FontSize ? `${heroData.ctaButton1FontSize}px` : undefined,
                        fontFamily: heroData.ctaButton1FontFamily,
                        fontWeight: heroData.ctaButton1FontWeight,
                        '&:hover': {
                          backgroundColor: heroData.ctaButton1BackgroundColor,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {heroData.ctaText1}
                    </Button>
                  )}
                  
                  {heroData.ctaText2 && (
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: heroData.ctaButton2BackgroundColor,
                        color: heroData.ctaButton2TextColor,
                        fontSize: heroData.ctaButton2FontSize ? `${heroData.ctaButton2FontSize}px` : undefined,
                        fontFamily: heroData.ctaButton2FontFamily,
                        fontWeight: heroData.ctaButton2FontWeight,
                        '&:hover': {
                          backgroundColor: heroData.ctaButton2BackgroundColor,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {heroData.ctaText2}
                    </Button>
                  )}
                </Box>

                {heroData.authorizedDealerTextContent && (
                  <Typography
                    variant="caption" 
                    sx={{
                      display: 'block',
                      mt: 3,
                      color: heroData.authorizedDealerTextColor,
                      fontSize: heroData.authorizedDealerTextFontSize ? `${heroData.authorizedDealerTextFontSize}px` : undefined,
                      fontFamily: heroData.authorizedDealerTextFontFamily,
                      fontWeight: heroData.authorizedDealerTextFontWeight,
                      textAlign: 'center',
                    }}
                  >
                    {heroData.authorizedDealerTextContent}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default HeroSectionEditor;
